import React, { useState } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import { format } from 'date-fns';

import { useServiceOrderExecutiveDashboard } from '../../../../hooks/report/useServiceOrderReport';

import OrderServiceFilters from './components/OrderServiceFilters';
import OrderServiceKpiGrid from './components/OrderServiceKpiGrid';
import OrderServiceStatusFlow from './components/OrderServiceStatusFlow';
import OrderServiceRiskAlerts from './components/OrderServiceRiskAlerts';
import OrderServiceValuationPanel from './components/OrderServiceValuationPanel';
import OrderServiceMonthlyEvolution from './components/OrderServiceMonthlyEvolution';
import OrderServiceProjectRanking from './components/OrderServiceProjectRanking';
import OrderServiceTypeRanking from './components/OrderServiceTypeRanking';

import { toNumber } from './utils/formatters';

const getDefaultFilters = () => {
    const currentYear = new Date().getFullYear();

    return {
        dateFrom: `${currentYear}-01-01`,
        dateTo: format(new Date(), 'yyyy-MM-dd'),
        proID: '',
        serProID: '',
    };
};

const OrderServiceReportDashboard = () => {
    const [filters, setFilters] = useState(getDefaultFilters());

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
    } = useServiceOrderExecutiveDashboard(filters);

    const summary = data?.summary || {};
    const byStatus = data?.byStatus || [];
    const byProject = data?.byProject || [];
    const valuationStatus = data?.valuationStatus || {};
    const monthlyEvolution = data?.monthlyEvolution || [];
    const serviceTypePerformance = data?.serviceTypePerformance || [];
    const riskAlerts = data?.riskAlerts || [];
    const topPendingValuation = data?.topPendingValuation || [];

    const totalOrders = toNumber(summary.total_orders);    

    const handleApplyFilters = (nextFilters) => {
        setFilters(nextFilters);
    };

    const handleResetFilters = () => {
        setFilters(getDefaultFilters());
    };

    if (isLoading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h4" fontWeight={900}>
                        Reporte estratégico de órdenes de servicio
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        Control gerencial de ejecución, valorización, facturación, cierre y riesgos.
                    </Typography>
                </Box>

                <OrderServiceFilters
                    filters={filters}
                    onApply={handleApplyFilters}
                    onReset={handleResetFilters}
                    isFetching={isFetching}
                />

                {isError && (
                    <Alert severity="error">
                        {error?.response?.data?.message || 'No se pudo cargar el reporte.'}
                    </Alert>
                )}

                {isFetching && (
                    <Alert severity="info">
                        Actualizando información del reporte...
                    </Alert>
                )}

                <OrderServiceKpiGrid summary={summary} />

                <OrderServiceStatusFlow
                    byStatus={byStatus}
                    totalOrders={totalOrders}
                    filters={filters}
                />

                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <OrderServiceRiskAlerts alerts={riskAlerts} />
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <OrderServiceValuationPanel
                            summary={summary}
                            valuationStatus={valuationStatus}
                            topPendingValuation={topPendingValuation}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <OrderServiceMonthlyEvolution data={monthlyEvolution} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <OrderServiceProjectRanking projects={byProject} />
                    </Grid>

                    <Grid item xs={12}>
                        <OrderServiceTypeRanking services={serviceTypePerformance} />
                    </Grid>
                </Grid>
            </Stack>
        </Box>
    );
};

export default OrderServiceReportDashboard;